import React, { memo, useState, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Image,
  FileText,
  Film,
  Zap,
  AlertCircle,
  Cpu,
  Plus,
  Trash2,
  Tag,
  FolderOpen,
  UploadCloud,
  Loader2,
  ArrowRight,
  Layers,
  Sparkles,
  Check,
  Eye,
  PlusCircle,
  Play,
  Copy,
  X,
  RefreshCw,
} from 'lucide-react';

export interface MediaVaultAsset {
  id: string;
  mediaType: 'image' | 'pdf' | 'video';
  title: string;
  tags: string;
  secureUrl: string;
  galleryUrls?: string[];
  vaultName?: string;
  description?: string;
}

export interface MediaVaultNodeData {
  assets?: MediaVaultAsset[];
  activeVault?: string;
  [key: string]: unknown;
}

const MAX_ASSETS = 50;

const MEDIA_TYPE_ICONS: Record<string, typeof Image> = {
  image: Image,
  pdf: FileText,
  video: Film,
};

const MEDIA_TYPE_COLORS: Record<string, string> = {
  image: 'text-sky-300 bg-sky-500/20 border-sky-500/40',
  pdf: 'text-rose-300 bg-rose-500/20 border-rose-500/40',
  video: 'text-violet-300 bg-violet-500/20 border-violet-500/40',
};

const detectMediaType = (url?: string, fallback: 'image' | 'pdf' | 'video' = 'image'): 'image' | 'pdf' | 'video' => {
  if (!url) return fallback;
  const cleanUrl = url.toLowerCase().split('?')[0].split('#')[0];
  if (cleanUrl.match(/\.(pdf|doc|docx|txt|xls|xlsx|csv)$/)) return 'pdf';
  if (cleanUrl.match(/\.(mp4|webm|mov|ogg|m4v|avi|mkv)$/)) return 'video';
  if (cleanUrl.match(/\.(jpg|jpeg|png|webp|gif|svg|bmp|ico)$/)) return 'image';
  return fallback;
};

export const MediaVaultNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as MediaVaultNodeData;
  const assets = Array.isArray(nodeData.assets) ? nodeData.assets : [];
  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);
  const [expanded, setExpanded] = useState(false);
  const [uploadingAssetId, setUploadingAssetId] = useState<string | null>(null);
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const [selectedVaultFilter, setSelectedVaultFilter] = useState<string>('all');
  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title?: string;
    type: 'image' | 'video' | 'pdf';
  } | null>(null);
  const debounceTimerRef = React.useRef<any>(null);

  // Extract all distinct vaults
  const distinctVaults = useMemo(() => {
    const set = new Set<string>();
    assets.forEach((a) => {
      if (a.vaultName && a.vaultName.trim()) set.add(a.vaultName.trim());
    });
    return Array.from(set);
  }, [assets]);

  const filteredAssets = useMemo(() => {
    if (selectedVaultFilter === 'all') return assets;
    return assets.filter((a) => (a.vaultName || '').trim() === selectedVaultFilter);
  }, [assets, selectedVaultFilter]);

  const persistNodeDataDirectly = (updatedAssets?: MediaVaultAsset[], extraUpdates?: Partial<MediaVaultNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          assets: updatedAssets || assets,
          ...(extraUpdates || {}),
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'KNOWLEDGE_UPLOAD',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[MediaVaultNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const updateAssets = (next: MediaVaultAsset[]) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, assets: next } } : n))
    );
    persistNodeDataDirectly(next);
  };

  const addAsset = (vaultName?: string) => {
    if (assets.length >= MAX_ASSETS) return;
    const next = [
      ...assets,
      {
        id: `asset-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
        mediaType: 'image' as const,
        title: '',
        tags: '',
        secureUrl: '',
        galleryUrls: [],
        vaultName: vaultName || (selectedVaultFilter !== 'all' ? selectedVaultFilter : 'General Vault'),
        description: '',
      },
    ];
    updateAssets(next);
  };

  const removeAsset = (assetId: string) => {
    updateAssets(assets.filter((a) => a.id !== assetId));
  };

  const updateAsset = (assetId: string, field: keyof MediaVaultAsset, value: any) => {
    updateAssets(assets.map((a) => (a.id === assetId ? { ...a, [field]: value } : a)));
  };

  const handleChange = (field: keyof MediaVaultNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(assets, { [field]: value });
  };

  const getAuthHeaders = () => {
    const headers: Record<string, string> = {};
    if (isTenantView) {
      if (authToken) headers['Authorization'] = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;
      const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
      if (!headers['Authorization'] && tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
    } else {
      const token = sessionStorage.getItem('adminToken') || '';
      const godToken = sessionStorage.getItem('godModeToken') || '';
      const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      else if (tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
      if (godToken) headers['X-God-Mode-Token'] = godToken;
    }
    return headers;
  };

  const handleFileUpload = async (assetId: string, file: File) => {
    setUploadingAssetId(assetId);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/admin/luna-flows/upload-asset', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const data = await res.json();

      if (data.success && data.url) {
        const fullUrl = data.url.startsWith('http')
          ? data.url
          : `${window.location.origin}${data.url}`;

        let detectedType: 'image' | 'pdf' | 'video' = 'image';
        if (data.mimeType) {
          if (data.mimeType.startsWith('image/')) detectedType = 'image';
          else if (data.mimeType === 'application/pdf') detectedType = 'pdf';
          else if (data.mimeType.startsWith('video/')) detectedType = 'video';
        }

        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== id) return n;
            const currentAssets: MediaVaultAsset[] = (n.data as any)?.assets || [];
            const updatedAssets = currentAssets.map((a: MediaVaultAsset) => {
              if (a.id !== assetId) return a;
              return {
                ...a,
                secureUrl: fullUrl,
                mediaType: detectedType,
                title: a.title || data.originalName?.replace(/\.[^/.]+$/, '') || 'Media Asset',
              };
            });
            return { ...n, data: { ...n.data, assets: updatedAssets } };
          })
        );

        persistNodeDataDirectly();
      } else {
        setUploadError(data.error || 'Upload failed.');
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Network error during upload.');
    } finally {
      setUploadingAssetId(null);
    }
  };

  const handleMultiFileUploadForAsset = async (assetId: string, files: FileList | File[]) => {
    if (!files || files.length === 0) return;
    setUploadingAssetId(assetId);
    setUploadError(null);

    try {
      const fileArray = Array.from(files);
      const uploadedUrls: string[] = [];

      for (const file of fileArray) {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/admin/luna-flows/upload-asset', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        });

        const data = await res.json();
        if (data.success && data.url) {
          const fullUrl = data.url.startsWith('http')
            ? data.url
            : `${window.location.origin}${data.url}`;
          uploadedUrls.push(fullUrl);
        }
      }

      if (uploadedUrls.length > 0) {
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== id) return n;
            const currentAssets: MediaVaultAsset[] = (n.data as any)?.assets || [];
            const updatedAssets = currentAssets.map((a: MediaVaultAsset) => {
              if (a.id !== assetId) return a;
              const mainUrl = a.secureUrl || uploadedUrls[0];
              const existingGallery = Array.isArray(a.galleryUrls) ? a.galleryUrls : [];
              const combinedGallery = Array.from(new Set([...existingGallery, ...uploadedUrls]));

              return {
                ...a,
                secureUrl: mainUrl,
                galleryUrls: combinedGallery,
              };
            });
            return { ...n, data: { ...n.data, assets: updatedAssets } };
          })
        );
        persistNodeDataDirectly();
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Error during multi-file upload.');
    } finally {
      setUploadingAssetId(null);
    }
  };

  const handleBatchCreateAssets = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;
    setIsBatchUploading(true);
    setUploadError(null);

    try {
      const fileArray = Array.from(files);
      const newCreatedAssets: MediaVaultAsset[] = [];

      for (const file of fileArray) {
        if (assets.length + newCreatedAssets.length >= MAX_ASSETS) break;

        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/admin/luna-flows/upload-asset', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        });

        const data = await res.json();
        if (data.success && data.url) {
          const fullUrl = data.url.startsWith('http')
            ? data.url
            : `${window.location.origin}${data.url}`;

          let detectedType: 'image' | 'pdf' | 'video' = 'image';
          if (data.mimeType) {
            if (data.mimeType.startsWith('image/')) detectedType = 'image';
            else if (data.mimeType === 'application/pdf') detectedType = 'pdf';
            else if (data.mimeType.startsWith('video/')) detectedType = 'video';
          }

          const rawTitle = data.originalName?.replace(/\.[^/.]+$/, '') || 'Media Asset';

          newCreatedAssets.push({
            id: `asset-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            mediaType: detectedType,
            title: rawTitle,
            tags: rawTitle.toLowerCase().replace(/[^a-z0-9]+/g, ', '),
            secureUrl: fullUrl,
            galleryUrls: [fullUrl],
            vaultName: selectedVaultFilter !== 'all' ? selectedVaultFilter : 'General Vault',
          });
        }
      }

      if (newCreatedAssets.length > 0) {
        const next = [...assets, ...newCreatedAssets];
        updateAssets(next);
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Batch creation error.');
    } finally {
      setIsBatchUploading(false);
    }
  };

  const typeIcon = (t: string) => MEDIA_TYPE_ICONS[t] || FileText;

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[420px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#0A1628]/98 via-[#060D1A]/98 to-[#03060E]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-cyan-400 ring-2 ring-cyan-400/50 shadow-[0_0_35px_rgba(6,182,212,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : 'border-cyan-500/35 hover:border-cyan-400/70 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(6,182,212,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
      }`}
    >
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('mediaVaultNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-950 via-slate-900 to-sky-950 border-2 border-cyan-400 text-cyan-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.8)] hover:border-cyan-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to manage Media Vault assets full-screen"
          >
            <FolderOpen size={13} className="text-cyan-300 animate-pulse" />
            <span>View Data in Portal</span>
            <ArrowRight size={13} className="text-cyan-400" />
          </button>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        id="in-media-vault"
        className="!bg-emerald-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(16,185,129,1)]"
        title="Input: Media Upload Event"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-media-vault"
        className="!bg-emerald-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(16,185,129,1)]"
        title="Output to Luna Core Hub (in-knowledge)"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
      />

      <div className="bg-gradient-to-r from-cyan-950/80 via-sky-950/60 to-slate-950/80 px-4 py-3 border-b border-cyan-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-sky-500/20 border border-sky-500/40 text-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.3)]">
            <FolderOpen size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Media Vault</h4>
            <p className="text-[10px] text-sky-300/70 font-mono">Multi-Asset & Multi-Vault</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-sky-400 animate-pulse bg-sky-500/10 px-2 py-0.5 rounded-full border border-sky-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30">
            MEDIA
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-sky-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Image size={14} className="text-sky-400" />
                <span className="font-bold text-white text-xs">
                  Multimodal Media Vault
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-sky-500/20 hover:bg-sky-500/35 text-sky-200 border border-sky-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-sky-300 font-bold">
                📁 {assets.length} Media Assets
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-300">
                {distinctVaults.length} Vaults
              </span>
              <span className="px-2 py-0.5 rounded-md bg-sky-950/70 border border-sky-500/30 text-[10px] font-mono text-sky-300">
                WhatsApp Dispatch
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3 text-xs">
        {/* AI Engine Badge */}
        <div className="p-2 rounded-xl bg-sky-950/40 border border-sky-500/20 flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-2 text-sky-200 font-medium">
            <Cpu size={14} className="text-sky-400" />
            <span>AI Multimodal Engine:</span>
          </div>
          <span className="font-mono text-[10px] font-bold text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/20">
            Training Engine
          </span>
        </div>

        {distinctVaults.length > 0 && (
          <div className="flex items-center gap-1 overflow-x-auto pb-1 text-[9px] font-mono scrollbar-none">
            <button
              type="button"
              onClick={() => setSelectedVaultFilter('all')}
              className={`nodrag px-2 py-0.5 rounded-md border transition cursor-pointer shrink-0 ${
                selectedVaultFilter === 'all'
                  ? 'bg-sky-500/30 border-sky-400 text-sky-200 font-bold'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              All Vaults ({assets.length})
            </button>
            {distinctVaults.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setSelectedVaultFilter(v)}
                className={`nodrag px-2 py-0.5 rounded-md border transition cursor-pointer shrink-0 truncate max-w-[120px] ${
                  selectedVaultFilter === v
                    ? 'bg-sky-500/30 border-sky-400 text-sky-200 font-bold'
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                }`}
                title={v}
              >
                📁 {v} ({assets.filter((a) => (a.vaultName || '').trim() === v).length})
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-slate-400 font-mono">
            {assets.length}/{MAX_ASSETS} assets registered
          </span>
          <div className="flex items-center gap-1.5">
            <label
              className={`nodrag flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                isBatchUploading
                  ? 'bg-sky-500/30 border-sky-400 text-sky-200'
                  : 'bg-gradient-to-r from-sky-500/20 to-cyan-500/20 border-sky-500/40 text-sky-300 hover:bg-sky-500/30'
              }`}
              title="Select multiple images/PDFs/videos at once to create multiple assets"
            >
              {isBatchUploading ? <Loader2 size={11} className="animate-spin" /> : <UploadCloud size={11} />}
              <span>{isBatchUploading ? 'Uploading...' : 'Multi-Upload'}</span>
              <input
                type="file"
                multiple
                className="hidden"
                accept="image/jpeg,image/png,image/webp,video/mp4,application/pdf"
                disabled={isBatchUploading}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleBatchCreateAssets(e.target.files);
                  }
                  e.target.value = '';
                }}
              />
            </label>

            <button
              type="button"
              onClick={() => addAsset()}
              disabled={assets.length >= MAX_ASSETS}
              className={`nodrag flex items-center gap-1 px-3 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                assets.length >= MAX_ASSETS
                  ? 'bg-slate-800/50 text-slate-500 border-white/5 cursor-not-allowed'
                  : 'bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30'
              }`}
            >
              <Plus size={11} /> + Add Card
            </button>
          </div>
        </div>

        {assets.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="nodrag w-full text-[10px] text-purple-300/80 hover:text-purple-200 font-mono text-center py-1 rounded-lg bg-purple-950/30 border border-purple-500/10 hover:border-purple-500/30 transition-all cursor-pointer"
          >
            {expanded
              ? '▲ Collapse Assets'
              : `▼ Expand ${filteredAssets.length} Media Card${filteredAssets.length !== 1 ? 's' : ''}${selectedVaultFilter !== 'all' ? ` in ${selectedVaultFilter}` : ''}`}
          </button>
        )}

        {expanded &&
          filteredAssets.map((asset) => {
            const cardUrls = Array.from(
              new Set(
                [
                  ...(Array.isArray(asset.galleryUrls) ? asset.galleryUrls : []),
                  ...(asset.secureUrl ? [asset.secureUrl] : []),
                ].filter(Boolean)
              )
            );

            return (
              <div
                key={asset.id}
                className="p-3.5 rounded-2xl bg-gradient-to-b from-[#110D26] to-[#0A0717] border border-purple-500/25 space-y-3 shadow-md"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-purple-950/80 border border-purple-500/30 text-purple-300 text-[10px] font-mono font-bold">
                      <Layers size={11} className="text-purple-400" />
                      <span>Images, Videos & Docs</span>
                    </div>
                    {asset.vaultName && (
                      <span className="text-[9px] font-mono text-purple-300 bg-purple-950/70 border border-purple-500/30 px-2 py-0.5 rounded-md truncate max-w-[100px]">
                        {asset.vaultName}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono text-slate-400 font-bold bg-slate-950/80 px-2 py-0.5 rounded border border-slate-800">
                      {cardUrls.length} file{cardUrls.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAsset(asset.id)}
                      className="nodrag p-1 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition-all cursor-pointer disabled:opacity-50"
                      title="Remove card"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                {/* Multi-Media Gallery Studio */}
                <div className="space-y-2 p-2.5 rounded-xl bg-slate-950/80 border border-purple-500/15">
                  {/* Multi-File Upload Dropzone */}
                  <label
                    className="nodrag flex items-center justify-center gap-1.5 p-2 rounded-xl bg-purple-950/40 hover:bg-purple-900/50 border border-dashed border-purple-500/40 hover:border-purple-400 text-purple-200 text-[10px] font-bold transition cursor-pointer shadow-sm group"
                  >
                    {uploadingAssetId === asset.id ? (
                      <Loader2 size={12} className="animate-spin text-purple-300" />
                    ) : (
                      <UploadCloud size={12} className="text-purple-400 group-hover:scale-110 transition-transform" />
                    )}
                    <span>+ Upload Media (Images, Videos, Docs)</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp,video/mp4,application/pdf"
                      disabled={uploadingAssetId !== null}
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          handleMultiFileUploadForAsset(asset.id, e.target.files);
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>

                  {/* Interactive Gallery Grid */}
                  {cardUrls.length > 0 ? (
                    <div className="grid grid-cols-4 gap-1.5 pt-0.5">
                      {cardUrls.map((gUrl, gIdx) => {
                        const gType = detectMediaType(gUrl, 'image');

                        return (
                          <div
                            key={gIdx}
                            onClick={() =>
                              setPreviewMedia({
                                url: gUrl,
                                title: `${asset.title || 'Media Asset'} #${gIdx + 1}`,
                                type: gType,
                              })
                            }
                            className="nodrag relative aspect-square rounded-xl bg-slate-950 border border-purple-500/30 hover:border-purple-400 overflow-hidden group/g flex items-center justify-center cursor-pointer shadow-md transition-all hover:scale-[1.04]"
                          >
                            {gType === 'pdf' ? (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-rose-950/30 text-rose-400 p-1 text-center">
                                <FileText size={16} className="text-rose-400 drop-shadow" />
                                <span className="text-[7px] font-mono font-bold text-rose-300 mt-0.5">PDF</span>
                              </div>
                            ) : gType === 'video' ? (
                              <div className="w-full h-full relative flex flex-col items-center justify-center bg-purple-950/30 text-violet-400">
                                <video src={gUrl} preload="metadata" className="w-full h-full object-cover opacity-60" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-5 h-5 rounded-full bg-purple-600/90 text-white flex items-center justify-center shadow">
                                    <Play size={9} className="fill-white ml-0.5" />
                                  </div>
                                </div>
                                <span className="absolute bottom-0.5 right-0.5 text-[6px] font-mono font-bold px-1 rounded bg-black/80 text-purple-300">VID</span>
                              </div>
                            ) : (
                              <img
                                src={gUrl}
                                alt={`gallery-${gIdx}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as any).src =
                                    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><text x="5" y="25" fill="%23999" font-size="9">Img</text></svg>';
                                }}
                              />
                            )}

                            {/* Type Tag Badge */}
                            <span className="absolute top-1 left-1 text-[6px] font-mono font-black px-1 py-0.2 rounded bg-black/80 text-purple-300 border border-purple-500/30 uppercase">
                              {gType}
                            </span>

                            {/* Hover Actions Overlay */}
                            <div
                              className="nodrag absolute inset-0 bg-slate-950/85 opacity-0 group-hover/g:opacity-100 flex items-center justify-center gap-1 transition-opacity z-10"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewMedia({
                                    url: gUrl,
                                    title: `${asset.title || 'Media Asset'} #${gIdx + 1}`,
                                    type: gType,
                                  })
                                }
                                className="p-1 rounded bg-purple-500/40 text-purple-200 hover:text-white hover:bg-purple-500 transition cursor-pointer"
                                title="Fullscreen Preview"
                              >
                                <Eye size={10} />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(gUrl);
                                  setSaveIndicator('📋 URL Copied!');
                                  setTimeout(() => setSaveIndicator(null), 2000);
                                }}
                                className="p-1 rounded bg-slate-800 text-slate-300 hover:text-white transition cursor-pointer"
                                title="Copy URL"
                              >
                                <Copy size={10} />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const remaining = cardUrls.filter((_, idx) => idx !== gIdx);
                                  updateAsset(asset.id, 'galleryUrls', remaining);
                                  if (asset.secureUrl === gUrl) {
                                    updateAsset(asset.id, 'secureUrl', remaining[0] || '');
                                  }
                                }}
                                className="p-1 rounded bg-rose-500/30 text-rose-300 hover:text-white hover:bg-rose-500 transition cursor-pointer disabled:opacity-50"
                                title="Remove"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-3 rounded-xl bg-slate-950/40 border border-dashed border-purple-500/20 text-center text-[10px] text-slate-500">
                      No media uploaded yet. Click <strong className="text-purple-300">+ Upload Media</strong>
                    </div>
                  )}
                </div>

                {/* Title, Vault & Tags Inputs */}
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-0.5">
                        Asset Title / Model
                      </label>
                      <input
                        type="text"
                        className="nodrag nowheel w-full bg-slate-950/90 border border-purple-500/20 rounded-lg px-2 py-1 text-slate-100 text-[10px] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-purple-400 placeholder:text-slate-600 font-sans"
                        placeholder="e.g. 2026 Model X"
                        value={asset.title}
                        onChange={(e) => updateAsset(asset.id, 'title', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-0.5">
                        Vault / Category
                      </label>
                      <input
                        type="text"
                        className="nodrag nowheel w-full bg-slate-950/90 border border-purple-500/20 rounded-lg px-2 py-1 text-purple-300 text-[10px] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-purple-400 placeholder:text-slate-600 font-sans"
                        placeholder="e.g. Vehicles"
                        value={asset.vaultName || ''}
                        onChange={(e) => updateAsset(asset.id, 'vaultName', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-0.5">
                      Trigger Tags (Comma Separated)
                    </label>
                    <div className="flex items-center gap-1.5">
                      <Tag size={10} className="text-purple-400 shrink-0" />
                      <input
                        type="text"
                        className="nodrag nowheel flex-1 bg-slate-950/90 border border-purple-500/20 rounded-lg px-2 py-1 text-purple-300 text-[10px] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-purple-400 placeholder:text-slate-600 font-mono"
                        placeholder="pricing, brochure, catalog, offer"
                        value={asset.tags}
                        onChange={(e) => updateAsset(asset.id, 'tags', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-0.5">
                      Asset Description / Marketing Pitch
                    </label>
                    <textarea
                      rows={3}
                      className="nodrag nowheel w-full bg-slate-950/90 border border-purple-500/20 rounded-lg px-2 py-1 text-slate-200 text-[10px] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-purple-400 placeholder:text-slate-600 font-sans resize-y"
                      placeholder="Full marketing description, certifications (ISO, GMP, FSSC), ingredients, dosage, benefits, and promotional copy..."
                      value={asset.description || ''}
                      onChange={(e) => updateAsset(asset.id, 'description', e.target.value)}
                    />
                  </div>
                </div>

                {uploadingAssetId === asset.id && (
                  <p className="text-[9px] text-purple-300 font-mono animate-pulse">Uploading file(s)...</p>
                )}
                {uploadError && uploadingAssetId === null && (
                  <p className="text-[9px] text-rose-400 font-mono">{uploadError}</p>
                )}
              </div>
            );
          })}

        <div className="flex items-center justify-between pt-1 border-t border-white/5">
          <span className="text-[10px] text-slate-400 font-medium">Dispatch Mode:</span>
          <span className="text-[10px] font-mono text-purple-300 bg-purple-950/60 px-2 py-0.5 rounded border border-purple-500/20">
            Multi-Asset WhatsApp Send
          </span>
        </div>
      </div>
      )}

      {/* High-Resolution Lightbox Modal */}
      {previewMedia && (
        <div
          className="nodrag fixed inset-0 z-[99999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-w-4xl w-full max-h-[90vh] bg-slate-950 border border-purple-500/40 rounded-3xl p-4 shadow-2xl flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex items-center justify-between pb-3 border-b border-purple-500/20">
              <h4 className="text-sm font-bold text-white font-mono truncate max-w-md">
                {previewMedia.title || 'Media Preview'}
              </h4>
              <button
                type="button"
                onClick={() => setPreviewMedia(null)}
                className="p-1.5 rounded-full bg-slate-800 text-slate-300 hover:text-white hover:bg-rose-600 transition cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <div className="w-full flex-1 flex items-center justify-center p-4 overflow-hidden min-h-[300px]">
              {previewMedia.type === 'image' ? (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.title}
                  className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-2xl"
                />
              ) : previewMedia.type === 'video' ? (
                <video
                  src={previewMedia.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-[70vh] rounded-xl shadow-2xl"
                />
              ) : (
                <iframe
                  src={previewMedia.url}
                  title={previewMedia.title}
                  className="w-full h-[65vh] rounded-xl border border-slate-800 bg-white"
                />
              )}
            </div>

            <div className="w-full pt-3 border-t border-purple-500/20 flex items-center justify-between text-xs text-slate-400">
              <span className="font-mono text-[10px] text-purple-300 truncate max-w-sm">
                {previewMedia.url}
              </span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(previewMedia.url);
                  setSaveIndicator('📋 URL Copied!');
                  setTimeout(() => setSaveIndicator(null), 2000);
                }}
                className="px-3 py-1 rounded-lg bg-purple-600 text-white font-bold text-xs hover:bg-purple-500 transition cursor-pointer"
              >
                Copy Direct URL
              </button>
            </div>
          </div>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-sky-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

MediaVaultNode.displayName = 'MediaVaultNode';
export default MediaVaultNode;
